<template>
  <div v-if="loaded">
    <header-component v-if="company && !isDashboardNew" :company="company" />
    <main
      :class="[
        isTrialExpired || isSubscriptionExpired
          ? 'trial-expired'
          : trialDaysLeft
          ? 'trial-not-expired'
          : '',
      ]"
    >
      <div v-if="isTrialExpired || isSubscriptionExpired" class="trial-bar">
        <p v-if="isTrialExpired">
          {{ label.get("your_free_trial_has_expired") }}
        </p>
        <p v-if="isSubscriptionExpired">
          {{ label.get("your_subscription_has_expired") }}
        </p>
        <a
          v-if="isTrialExpired && canViewSubscribe"
          href="https://dashboard.attotime.com/subscription/plans"
          class="atto-btn white-btn"
          >{{ label.get("subscribe_now") }}</a
        >
        <a
          v-if="isSubscriptionExpired && canViewSubscribe"
          href="https://dashboard.attotime.com/subscription/plans"
          class="atto-btn re-subscribe"
          >{{ label.get("reactivate_subscription") }}</a
        >
      </div>
      <div v-if="isIE" class="trial-bar">
        <p class="ie">
          You’re using a browser we don’t support. Try using an updated browser
          to have a better experience on Atto.
        </p>
      </div>
      <!--<router-view :key="'main-' + $route.fullPath" />-->
      <router-view></router-view>
      <transition name="fade">
        <span v-if="overlay" class="main-overlay" />
      </transition>
    </main>

    <time-clock-widget-component
      v-if="timeClockEnabled && !isMobile && !isDashboardNew"
    />

    <loader-component v-show="loading" />
    <delete-entry-component v-if="entry" :timesheet="entry" />
    <invite-member-component v-if="showInvite" />
    <expired-trial-component v-if="showNoSubscription" />

    <create-manual-entry-component
      v-if="addNewEntry"
      :date="selectedDate"
      :member="member"
    />
    <edit-manual-entry-component
      v-if="editEntry"
      :entry="timesheet"
      :member="member"
    />

    <edit-entries-component
      v-if="editEntries"
      :entries="entries"
      :member="member"
    />
    <delete-entries-component
      v-if="deleteEntries"
      :entries="entries"
      :member="member"
    />

    <manual-activity-component v-if="manualClockInOut" :activity="activity" />

    <session-map-component v-if="extendedMap" :id="timesheet" />
    <subscription-successful-component v-if="subscriptionSuccessful" />
    <reactivate-subscription-component v-if="reactivateSubscription" />
    <!-- <download-app-component v-if="showDownloadApp" /> -->
    <disconnect-quickbook-account-component v-if="showDisconnectQuickbook" />

    <disconnect-xero-account-component v-if="showDisconnectXero" />

    <import-quick-books-component v-if="showQuickBookImportModal" />

    <import-xero-employees-component v-if="showXeroEmployeesImportModal" />

    <import-xero-pay-items-component v-if="showXeroPayItemsImportModal" />

    <xero-integration-steps-component v-if="showXeroIntegrationModel" />

    <export-xero-timesheets-component v-if="showXeroTimesheetsExportModal" />

    <import-xero-pay-calendars-component v-if="showXeroPayCalendarsModal" />

    <xero-overwrite-data-modal
      v-if="showXeroOverwriteDataModal"
      :xeroStartDay="xeroStartDay"
      :payrollCalendarId="xeroPayrollCalendarId"
    />

    <xero-uk400-error-modal v-if="showXeroUk400Error" />

    <quick-books-successful-connected-component
      v-if="showQuickBooksSuccessfulConnectedModal"
    />

    <xero-successful-connected-component
      v-if="showXeroSuccessfulConnectedModal"
    />

    <show-xero-errors-modal-component v-if="showXeroErrorsModal" />

    <xero-export-will-take-time-modal v-if="showXeroExportWillTakeTime" />

    <xero-not-connected-modal-component v-if="showXeroNotConnected" />

    <purchase-seats-quick-books-component
      v-if="showPurchaseSeatsQuickBooksModal"
      :invites="invites"
      :prorated-cost-per-user="proratedCostPerUser"
      :is-first-purchase-stripe-tax="isFirstPurchaseStripeTax"
    />

    <add-job-code-component v-if="showAddJobCodes" />
    <add-break-code-component v-if="showAddBreakCodes" />

    <!-- <whats-new-component v-if="showWhatsNew" /> -->
  </div>
</template>

<script>
import HeaderComponent from "../header/HeaderComponent.vue";
import LoaderComponent from "../../shared/components/LoaderComponent";
import DeleteEntryComponent from "../../shared/components/DeleteEntryComponent";
import InviteMemberComponent from "../../shared/components/InviteMemberComponent";
import ExpiredTrialComponent from "../../shared/components/ExpiredTrialComponent";
import CreateManualEntryComponent from "../../features/timesheets/shared/CreateManualEntryComponent";
import EditManualEntryComponent from "../../features/timesheets/shared/EditManualEntryComponent";
import EditEntriesComponent from "../../features/timesheets/shared/EditEntriesComponent";
import DeleteEntriesComponent from "../../features/timesheets/shared/components/DeleteEntriesComponent";
import TimesheetService from "../../features/timesheets/shared/TimesheetService";
import ManualActivityComponent from "../../features/team-activity/components/ManualActivityComponent";
import SessionMapComponent from "../../features/timesheets/shared/SessionMapComponent";
import SubscriptionSuccessfulComponent from "../../features/subscription/components/SubscriptionSuccessfulComponent";
import ReactivateSubscriptionComponent from "../../features/settings/billing/components/ReactivateSubscriptionComponent";
import DisconnectQuickbookAccountComponent from "../../features/settings/company/components/DisconnectQuickbookAccountComponent";
import ImportQuickBooksComponent from "../../features/settings/company/components/ImportQuickBooksComponent";
import PurchaseSeatsQuickBooksComponent from "../../features/settings/company/components/PurchaseSeatsQuickbooksComponent";
import DownloadAppComponent from "../../shared/components/DownloadAppComponent";
import FeedbackSupportComponent from "../../shared/components/FeedbackSupportComponent";
import QuickBooksSuccessfulConnectedComponent from "../../features/settings/company/components/QuickBooksSuccessfulConnectedComponent";
import UtilsService from "../../shared/services/UtilsService";
import ImportXeroEmployeesComponent from "../../features/settings/company/components/ImportXeroEmployeesComponent";
import ImportXeroPayItemsComponent from "../../features/settings/company/components/ImportXeroPayItemsComponent";
import XeroSuccessfulConnectedComponent from "../../features/settings/company/components/XeroSuccessfulConnectedComponent";
import DisconnectXeroAccountComponent from "../../features/settings/company/components/DisconnectXeroAccountComponent";
import ExportXeroTimesheetsComponent from "../../features/settings/company/components/ExportXeroTimesheetsComponent";
import ShowXeroErrorsModalComponent from "../../features/settings/company/components/ShowXeroErrorsModal";
import ImportXeroPayCalendarsComponent from "../../features/settings/company/components/ImportXeroPayCalendarsComponent";
import XeroNotConnectedModalComponent from "../../features/settings/company/components/XeroNotConnectedModal";
import XeroIntegrationStepsComponent from "../../features/settings/company/components/XeroIntegrationStepsComponent";
import XeroExportWillTakeTimeModal from "../../features/settings/company/components/XeroExportWillTakeTimeModal";
import XeroOverwriteDataModal from "../../features/settings/company/components/XeroOverwriteDataModal";
import XeroUk400ErrorModal from "../../features/settings/company/components/XeroUk400ErrorModal";
import TimeClockWidgetComponent from "../../features/time-clock/TimeClockWidgetComponent";
import AddJobCodeComponent from "../../features/settings/job-codes/components/AddJobCodeComponent";
import AddBreakCodeComponent from "../../features/settings/break-preferences/components/AddBreakCodeComponent";
import WhatsNewComponent from "../../shared/components/WhatsNewComponent";

export default {
  name: "BaseComponent",
  components: {
    WhatsNewComponent,
    TimeClockWidgetComponent,
    XeroUk400ErrorModal,
    XeroOverwriteDataModal,
    XeroExportWillTakeTimeModal,
    XeroIntegrationStepsComponent,
    XeroNotConnectedModalComponent,
    ImportXeroPayCalendarsComponent,
    ShowXeroErrorsModalComponent,
    ExportXeroTimesheetsComponent,
    DisconnectXeroAccountComponent,
    XeroSuccessfulConnectedComponent,
    ImportXeroPayItemsComponent,
    ImportXeroEmployeesComponent,
    QuickBooksSuccessfulConnectedComponent,

    HeaderComponent,
    LoaderComponent,
    DeleteEntryComponent,
    InviteMemberComponent,
    ExpiredTrialComponent,
    CreateManualEntryComponent,
    EditManualEntryComponent,
    EditEntriesComponent,
    DeleteEntriesComponent,
    ManualActivityComponent,
    SessionMapComponent,
    SubscriptionSuccessfulComponent,
    ReactivateSubscriptionComponent,
    DownloadAppComponent,
    FeedbackSupportComponent,
    DisconnectQuickbookAccountComponent,
    ImportQuickBooksComponent,
    PurchaseSeatsQuickBooksComponent,
    AddJobCodeComponent,
    AddBreakCodeComponent,
  },
  data() {
    return {
      entry: null,
      showInvite: false,
      showNoSubscription: false,
      subscribed: true,
      subscriptionExpired: false,
      overlay: false,
      member: null,
      addNewEntry: false,
      selectedDate: undefined,
      editEntry: false,
      timesheet: null,
      editEntries: false,
      deleteEntries: false,
      entries: null,
      manualClockInOut: false,
      activity: null,
      extendedMap: false,
      subscriptionSuccessful: false,
      reactivateSubscription: false,
      showDisconnectQuickbook: false,
      showDisconnectXero: false,
      showQuickBookImportModal: false,
      showXeroEmployeesImportModal: false,
      showXeroPayItemsImportModal: false,
      showXeroIntegrationModel: false,
      showXeroTimesheetsExportModal: false,
      showXeroPayCalendarsModal: false,
      showXeroOverwriteDataModal: false,
      showXeroUk400Error: false,
      xeroStartDay: null,
      xeroPayrollCalendarId: null,
      showXeroErrorsModal: false,
      showXeroExportWillTakeTime: false,
      showXeroNotConnected: false,
      showPurchaseSeatsQuickBooksModal: false,
      showQuickBooksSuccessfulConnectedModal: false,
      showXeroSuccessfulConnectedModal: false,
      invites: null,
      proratedCostPerUser: 7,
      isFirstPurchaseStripeTax: false,
      showDownloadApp: false,
      feedback: null,
      isIE: UtilsService.isIE.Ten(),
      isMobile: UtilsService.isMobile.any(),
      trialDaysLeft: null,
      showAddJobCodes: false,
      showAddBreakCodes: false,
      showWhatsNew: false,
    };
  },
  computed: {
    loaded() {
      document.body.classList.add(window.locale);
      const initialLoader = document.getElementById("initial-loader");
      if (initialLoader !== undefined && initialLoader !== null) {
        initialLoader.remove();
      }
      if (this.company !== null) {
        this.trialDaysLeft = this.company.trial_days_left;
      }
      return this.$store.state.loaded;
    },
    isAuth() {
      return this.$store.state.authenticated;
    },
    loading() {
      return this.$store.state.loading;
    },
    company() {
      try {
        const quickBooksParam = this.$route.query.quickbook;
        const xeroParam = this.$route.query.xero;
        const xeroNotConnected = this.$route.query.xeroNotConnected;
        if (quickBooksParam !== undefined && quickBooksParam !== null) {
          this.showQuickBookImport();
        } else if (xeroParam !== undefined && xeroParam !== null) {
          this.showXeroIntegration();
        } else if (
          xeroNotConnected !== undefined &&
          xeroNotConnected !== null
        ) {
          this.showXeroNotConnectedModal();
        } else {
          this.shouldPopUpDownloadAppModal();
        }
      } catch (e) {}

      return this.$store.state.company;
    },
    isSubscriptionExpired() {
      return (
        !this.$store.state.onTrial &&
        !this.$store.state.subscribed &&
        !this.$store.state.company.trial_ends_at
      );
    },
    isTrialExpired() {
      return (
        !this.$store.state.onTrial &&
        !this.$store.state.subscribed &&
        this.$store.state.company.trial_ends_at
      );
    },
    canViewSubscribe() {
      return this.$store.state.permission.canAccessBilling;
    },
    timeClockEnabled() {
      return this.$store.state.companySettings.web_timeclock;
    },
    isDashboardNew() {
      const search = window.location.search;
      const params = new URLSearchParams(search);
      const isToken = params.get("token");

      return !!isToken;
    },
  },
  mounted() {
    this.$root.$on("onDeleteEntry", this.onDeleteEntry);
    this.$root.$on("showInviteMembers", this.showInviteMembers);
    this.$root.$on("hideInviteMembers", this.hideInviteMembers);
    this.$root.$on("noSubscription", this.showNoSubscriptionModal);
    this.$root.$on("noSubscriptionHide", this.hideNoSubscriptionModal);

    this.$root.$on("showCreateEntry", this.addNewTimeEntry);
    this.$root.$on("hideCreateEntry", this.hideCreateEntry);

    this.$root.$on("showEditEntry", this.showEditEntry);
    this.$root.$on("hideEditEntry", this.hideEditEntry);

    this.$root.$on("showEditEntries", this.showEditEntries);
    this.$root.$on("hideEditEntries", this.hideEditEntries);

    this.$root.$on("showDeleteEntries", this.showDeleteEntries);
    this.$root.$on("hideDeleteEntries", this.hideDeleteEntries);

    this.$root.$on("showManualClockInOut", this.showManualClockInOut);
    this.$root.$on("hideManualClockInOut", this.hideManualClockInOut);

    this.$root.$on("showExtendedMap", this.showExtendedMap);
    this.$root.$on("hideExtendedMap", this.hideExtendedMap);

    this.$root.$on(
      "showSubscriptionSuccessful",
      this.showSubscriptionSuccessful
    );
    this.$root.$on(
      "hideSubscriptionSuccessful",
      this.hideSubscriptionSuccessful
    );

    this.$root.$on(
      "showReactivateSubscription",
      this.showReactivateSubscription
    );
    this.$root.$on(
      "hideReactivateSubscription",
      this.hideReactivateSubscription
    );

    this.$root.$on(
      "showDisconnectQuickbookAccount",
      this.showDisconnectQuickbookAccount
    );
    this.$root.$on(
      "hideDisconnectQuickbookAccount",
      this.hideDisconnectQuickbookAccount
    );

    this.$root.$on("showDisconnectXeroAccount", this.showDisconnectXeroAccount);
    this.$root.$on("hideDisconnectXeroAccount", this.hideDisconnectXeroAccount);

    this.$root.$on("showQuickBookImport", this.showQuickBookImport);
    this.$root.$on("hideQuickBookImport", this.hideQuickBookImport);

    this.$root.$on("showXeroEmployeeImport", this.showXeroEmployeeImport);
    this.$root.$on("hideXeroEmployeeImport", this.hideXeroEmployeeImport);

    this.$root.$on("showXeroPayItemsImport", this.showXeroPayItemsImport);
    this.$root.$on("hideXeroPayItemsImport", this.hideXeroPayItemsImport);

    this.$root.$on("showXeroIntegration", this.showXeroIntegration);
    this.$root.$on("hideXeroIntegration", this.hideXeroIntegration);

    this.$root.$on("showXeroTimesheetsExport", this.showXeroTimesheetsExport);
    this.$root.$on("hideXeroTimesheetsExport", this.hideXeroTimesheetsExport);

    this.$root.$on("showXeroPayCalendars", this.showXeroPayCalendars);
    this.$root.$on("hideXeroPayCalendars", this.hideXeroPayCalendars);

    this.$root.$on("showXeroOverwriteModal", this.showXeroOverwriteModal);
    this.$root.$on("hideXeroOverwriteModal", this.hideXeroOverwriteModal);

    this.$root.$on("showXeroUk400ErrorModal", this.showXeroUk400ErrorModal);
    this.$root.$on("hideXeroUk400ErrorModal", this.hideXeroUk400ErrorModal);

    this.$root.$on("showXeroErrors", this.showXeroErrors);
    this.$root.$on("hideXeroErrors", this.hideXeroErrors);

    this.$root.$on(
      "showXeroExportWillTakeTimeModal",
      this.showXeroExportWillTakeTimeModal
    );
    this.$root.$on(
      "hideXeroExportWillTakeTimeModal",
      this.hideXeroExportWillTakeTimeModal
    );

    this.$root.$on("showXeroNotConnectedModal", this.showXeroNotConnectedModal);
    this.$root.$on("hideXeroNotConnectedModal", this.hideXeroNotConnectedModal);

    this.$root.$on(
      "showPurchaseSeatsQuickBooks",
      this.showPurchaseSeatsQuickBooks
    );
    this.$root.$on(
      "hidePurchaseSeatsQuickBooks",
      this.hidePurchaseSeatsQuickBooks
    );

    this.$root.$on("hideDownloadApp", this.hideDownloadApp);

    this.$root.$on("showFeedbackModal", this.showFeedbackModal);
    this.$root.$on("hideFeedbackModal", this.hideFeedbackSupportModal);

    this.$root.$on(
      "showQuickBooksSuccessfulConnected",
      this.showQuickBooksSuccessfulConnected
    );
    this.$root.$on(
      "hideQuickBooksSuccessfulConnected",
      this.hideQuickBooksSuccessfulConnected
    );

    this.$root.$on(
      "showXeroSuccessfulConnected",
      this.showXeroSuccessfulConnected
    );
    this.$root.$on(
      "hideXeroSuccessfulConnected",
      this.hideXeroSuccessfulConnected
    );

    this.$root.$on("showAddJobCode", this.showAddJobCode);
    this.$root.$on("hideAddJobCode", this.hideAddJobCode);

    this.$root.$on("showAddBreakCode", this.showAddBreakCode);
    this.$root.$on("hideAddBreakCode", this.hideAddBreakCode);

    this.$root.$on("hideWhatsNew", this.hideWhatsNew);
  },
  beforeDestroy() {
    this.$root.$off("onDeleteEntry");
    this.$root.$off("showInviteMembers");
    this.$root.$off("hideInviteMembers");
    this.$root.$off("noSubscription");
    this.$root.$off("noSubscriptionHide");

    this.$root.$off("showCreateEntry");
    this.$root.$off("hideCreateEntry");

    this.$root.$off("showEditEntry");
    this.$root.$off("hideEditEntry");

    this.$root.$off("showEditEntries");
    this.$root.$off("hideEditEntries");

    this.$root.$off("showDeleteEntries");
    this.$root.$off("hideDeleteEntries");

    this.$root.$off("showManualClockInOut");
    this.$root.$off("hideManualClockInOut");

    this.$root.$off("showExtendedMap");
    this.$root.$off("hideExtendedMap");

    this.$root.$off("showSubscriptionSuccessful");
    this.$root.$off("hideSubscriptionSuccessful");

    this.$root.$off("showDisconnectQuickbookAccount");
    this.$root.$off("hideDisconnectQuickbookAccount");

    this.$root.$off("showDisconnectXeroAccount");
    this.$root.$off("hideDisconnectXeroAccount");

    this.$root.$off("showQuickBookImport");
    this.$root.$off("hideQuickBookImport");

    this.$root.$off("showXeroEmployeeImport");
    this.$root.$off("hideXeroEmployeeImport");

    this.$root.$off("showXeroPayItemsImport");
    this.$root.$off("hideXeroPayItemsImport");

    this.$root.$off("showXeroIntegration");
    this.$root.$off("hideXeroIntegration");

    this.$root.$off("showXeroTimesheetsExport");
    this.$root.$off("hideXeroTimesheetsExport");

    this.$root.$off("showXeroPayCalendars");
    this.$root.$off("hideXeroPayCalendars");

    this.$root.$off("showXeroOverwriteModal");
    this.$root.$off("hideXeroOverwriteModal");

    this.$root.$off("showXeroUk400ErrorModal");
    this.$root.$off("hideXeroUk400ErrorModal");

    this.$root.$off("showXeroErrors");
    this.$root.$off("hideXeroErrors");

    this.$root.$off("showXeroExportWillTakeTimeModal");
    this.$root.$off("hideXeroExportWillTakeTimeModal");

    this.$root.$off("showXeroNotConnectedModal");
    this.$root.$off("hideXeroNotConnectedModal");

    this.$root.$off("showPurchaseSeatsQuickBooks");
    this.$root.$off("hidePurchaseSeatsQuickBooks");

    this.$root.$off("hideDownloadApp");

    this.$root.$off("showFeedbackModal");
    this.$root.$off("hideFeedbackModal");

    this.$root.$off("showQuickBooksSuccessfulConnected");
    this.$root.$off("hideQuickBooksSuccessfulConnected");

    this.$root.$off("showXeroSuccessfulConnected");
    this.$root.$off("hideXeroSuccessfulConnected");

    this.$root.$off("showAddJobCode");
    this.$root.$off("hideAddJobCode");

    this.$root.$off("hideWhatsNew");
  },
  methods: {
    onDeleteEntry(entry) {
      this.entry = entry;
    },
    showInviteMembers() {
      this.showInvite = true;
    },
    hideInviteMembers() {
      this.showInvite = false;
    },
    showNoSubscriptionModal() {
      this.showNoSubscription = true;
    },
    hideNoSubscriptionModal() {
      this.showNoSubscription = false;
    },
    addNewTimeEntry(data) {
      if (!this.$store.state.subscribed && !this.$store.state.onTrial) {
        this.showNoSubscription = true;
        return;
      }

      this.member = data.member;
      this.selectedDate = data.selectedDate;
      this.addNewEntry = true;
    },
    hideCreateEntry() {
      this.addNewEntry = false;
      this.selectedDate = undefined;
      this.member = null;
    },
    showEditEntry(data) {
      if (!this.$store.state.subscribed && !this.$store.state.onTrial) {
        this.showNoSubscription = true;
        return;
      }

      this.member = data.member;
      this.timesheet =
        data.entry.time_entry !== null
          ? data.entry.time_entry
          : data.entry.time_off_entry;
      this.editEntry = true;
    },
    hideEditEntry() {
      this.editEntry = false;
      this.timesheet = null;
    },
    showEditEntries(data) {
      if (!this.$store.state.subscribed && !this.$store.state.onTrial) {
        this.showNoSubscription = true;
        return;
      }

      this.entries = data.entries;
      this.member = data.member;
      this.editEntries = true;
    },
    hideEditEntries(data) {
      if (data !== undefined) {
        this.$store.commit("showLoading");
        TimesheetService.getTimeEntryForEdit(data.entry.id)
          .then((result) => {
            this.$store.commit("hideLoading");
            this.member = data.member;
            this.timesheet = result;
            this.editEntry = true;
          })
          .catch((error) => {});
      }
      this.editEntries = false;
      this.entries = null;
      this.member = null;
    },
    showDeleteEntries(data) {
      if (!this.$store.state.subscribed && !this.$store.state.onTrial) {
        this.showNoSubscription = true;
        return;
      }

      this.entries = data.entries;
      this.member = data.member;
      this.deleteEntries = true;
    },
    hideDeleteEntries() {
      this.deleteEntries = false;
      this.entries = null;
      this.member = null;
    },
    showManualClockInOut(activity) {
      this.activity = activity;
      this.manualClockInOut = true;
    },
    hideManualClockInOut() {
      this.manualClockInOut = false;
      this.activity = null;
    },
    showExtendedMap(id) {
      this.timesheet = id;
      this.extendedMap = true;
    },
    hideExtendedMap() {
      this.extendedMap = false;
      this.timesheet = null;
    },
    showSubscriptionSuccessful() {
      this.subscriptionSuccessful = true;
    },
    hideSubscriptionSuccessful() {
      this.subscriptionSuccessful = false;
    },
    showReactivateSubscription() {
      this.reactivateSubscription = true;
    },
    hideReactivateSubscription() {
      this.reactivateSubscription = false;
    },

    showDisconnectQuickbookAccount() {
      this.showDisconnectQuickbook = true;
    },

    hideDisconnectQuickbookAccount() {
      this.showDisconnectQuickbook = false;
    },

    showDisconnectXeroAccount() {
      this.showDisconnectXero = true;
    },

    hideDisconnectXeroAccount() {
      this.showDisconnectXero = false;
    },

    showQuickBookImport() {
      this.showQuickBookImportModal = true;
    },

    showXeroEmployeeImport() {
      this.showXeroEmployeesImportModal = true;
    },

    showXeroPayItemsImport() {
      this.showXeroPayItemsImportModal = true;
    },

    hideXeroPayItemsImport() {
      const _this = this;
      _this.showXeroPayItemsImportModal = false;
    },

    showXeroIntegration() {
      this.showXeroIntegrationModel = true;
    },

    hideXeroIntegration() {
      this.showXeroIntegrationModel = false;
    },

    showXeroPayCalendars() {
      this.showXeroPayCalendarsModal = true;
    },

    hideXeroPayCalendars() {
      this.showXeroPayCalendarsModal = false;
    },

    showXeroOverwriteModal() {
      this.showXeroOverwriteDataModal = true;
    },

    hideXeroOverwriteModal(data) {
      this.showXeroOverwriteDataModal = false;

      if (data && data.queueIsActive) {
        setTimeout(() => {
          this.showXeroExportWillTakeTime = true;
        }, 300);
      }
    },

    showXeroUk400ErrorModal() {
      this.showXeroUk400Error = true;
    },

    hideXeroUk400ErrorModal() {
      this.showXeroUk400Error = false;
    },

    showXeroTimesheetsExport() {
      this.showXeroTimesheetsExportModal = true;
    },

    hideXeroTimesheetsExport(data) {
      this.showXeroTimesheetsExportModal = false;

      if (data && data.showOverwriteDataModal) {
        setTimeout(() => {
          this.showXeroOverwriteDataModal = true;
          this.xeroStartDay = data.xeroStartDay;
          this.xeroPayrollCalendarId = data.payroll_calendar_id;
        }, 300);
      }

      if (data && data.queueIsActive) {
        setTimeout(() => {
          this.showXeroExportWillTakeTime = true;
        }, 300);
      }
    },

    hideXeroEmployeeImport(data) {
      const _this = this;
      _this.showXeroEmployeesImportModal = false;
      if (data && data.showPurchaseSeats) {
        setTimeout(() => {
          _this.invites = data.invites;
          _this.proratedCostPerUser = data.proratedCostPerUser;
          _this.isFirstPurchaseStripeTax = data.isFirstPurchaseStripeTax;
          _this.showPurchaseSeatsQuickBooksModal = true;
        }, 300);
      }
    },
    showXeroErrors() {
      this.showXeroErrorsModal = true;
    },

    hideXeroErrors() {
      this.showXeroErrorsModal = false;
    },

    showXeroExportWillTakeTimeModal() {
      this.showXeroExportWillTakeTime = true;
    },

    hideXeroExportWillTakeTimeModal() {
      this.showXeroExportWillTakeTime = false;
    },

    showXeroNotConnectedModal() {
      this.showXeroNotConnected = true;
    },

    hideXeroNotConnectedModal() {
      this.showXeroNotConnected = false;
    },

    hideQuickBookImport(data) {
      const _this = this;
      this.showQuickBookImportModal = false;
      if (data && data.showPurchaseSeats) {
        setTimeout(() => {
          _this.invites = data.invites;
          _this.proratedCostPerUser = data.proratedCostPerUser;
          _this.isFirstPurchaseStripeTax = data.isFirstPurchaseStripeTax;
          _this.showPurchaseSeatsQuickBooksModal = true;
        }, 300);
      }
      if (data && data.quickbookConnected) {
        setTimeout(() => {
          _this.showQuickBooksSuccessfulConnectedModal = true;
        }, 300);
      }
    },

    showPurchaseSeatsQuickBooks() {
      this.showPurchaseSeatsQuickBooksModal = true;
    },
    hidePurchaseSeatsQuickBooks(data) {
      this.showPurchaseSeatsQuickBooksModal = false;
      if (data && data.quickbookConnected) {
        setTimeout(() => {
          this.showQuickBooksSuccessfulConnectedModal = true;
        }, 300);
      }
    },
    shouldPopUpDownloadAppModal() {
      const isMobile = UtilsService.isMobile.any();
      if (!isMobile && this.$store.state.user !== null) {
        setTimeout(() => {
          const introSteps = this.$store.state.user.intro_step;
          if (
            introSteps !== undefined &&
            introSteps.download_app !== undefined &&
            !introSteps.download_app
          ) {
            this.showDownloadApp = true;
          } else if (
            introSteps !== undefined &&
            introSteps.web_time_clock !== undefined &&
            !introSteps.web_time_clock
          ) {
            this.showWhatsNew = true;
          }
        }, 3000);
      }
    },
    hideDownloadApp() {
      this.showDownloadApp = false;
      const introSteps = this.$store.state.user.intro_step;
      if (
        introSteps !== undefined &&
        introSteps.web_time_clock !== undefined &&
        !introSteps.web_time_clock
      ) {
        this.showWhatsNew = true;
      } else {
        this.$root.$emit("onToggleTimeClock");
      }
    },

    showFeedbackModal() {
      this.feedback = true;
    },

    hideFeedbackSupportModal() {
      this.feedback = false;
    },
    showQuickBooksSuccessfulConnected() {
      this.showQuickBooksSuccessfulConnectedModal = true;
    },
    hideQuickBooksSuccessfulConnected() {
      this.showQuickBooksSuccessfulConnectedModal = false;
    },
    showXeroSuccessfulConnected() {
      this.showXeroSuccessfulConnectedModal = true;
    },
    hideXeroSuccessfulConnected() {
      this.showXeroSuccessfulConnectedModal = false;
    },
    showAddJobCode() {
      this.showAddJobCodes = true;
    },
    hideAddJobCode(jobs) {
      if (jobs) {
        jobs.forEach((item) => {
          this.$store.state.companyJobs.push(item);
        });
      }
      this.showAddJobCodes = false;
    },
    showAddBreakCode() {
      this.showAddBreakCodes = true;
    },
    hideAddBreakCode(breaks) {
      if (breaks) {
        breaks.forEach((item) => {
          this.$store.state.breakCodes.push(item);
        });
      }
      this.showAddBreakCodes = false;
    },
    hideWhatsNew() {
      this.showWhatsNew = false;
      this.$root.$emit("onToggleTimeClock");
    },
  },
};
</script>
<style lang="scss">
.dashboard-new {
  #import-quickbooks___BV_modal_outer_,
  #export-xero-timesheets___BV_modal_outer_,
  #import-xero___BV_modal_outer_,
  #import-xero-pay-calendars,
  #import-xero-pay-items___BV_modal_outer_ {
    div#import-quickbooks {
      padding: 0 !important;
    }
    .modal-dialog.modal-lg-size.modal-dialog-centered {
      max-width: 100vw;
      max-height: 100vh;
      min-height: 100vh;
      min-width: 100vw;
      margin: 0;
    }

    .modal-content {
      min-height: 100vh;
    }
  }
}

.hide-dashboard-new-sidebar {
  background-color: #fff;
  .settings-sidebar.sidebar {
    display: none !important;
  }
}

main.trial-not-expired {
  margin-top: 109px;
  height: calc(100vh - 109px);
  .activities {
    .grid-container {
      height: calc(100vh - 109px);
      max-height: 100%;
      .left-aside {
        .wrapper {
          height: calc(100vh - 109px);
        }
      }
    }
  }
}
main {
  margin-top: 61px;
  position: relative;
  z-index: 10;
  height: calc(100vh - 61px);
  .activities {
    .grid-container {
      height: calc(100vh - 61px);
      max-height: 100%;
      .left-aside {
        .wrapper {
          height: calc(100vh - 61px);
        }
      }
    }
  }
  .ps {
    .ps__rail-y {
      width: 16px;
      opacity: 1;
      z-index: 11;

      &:hover {
        background-color: transparent;

        .ps__thumb-y {
          width: 8px;
          background-color: rgba(239, 239, 239, 1);
        }
      }

      &.ps--clicking {
        background-color: transparent;
      }
    }

    .ps__thumb-y {
      right: 4px;
      width: 8px;
      opacity: 1;
      background-color: rgba(239, 239, 239, 0.5);
    }
  }

  .trial-bar {
    width: 100%;
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    background-color: #e05656;
    position: fixed;
    top: 60px;
    left: 0;
    z-index: 4;
    min-height: 48px;

    p {
      color: #fff;
      margin-bottom: 0;
      margin-right: 24px;
      font-family: "Roboto", sans-serif;
      font-size: 0.875rem;
      line-height: 1rem;
      font-weight: 500;
      &.ie {
        line-height: 2rem;
      }
    }
  }

  &.trial-expired {
    margin-top: 107px;
    height: calc(100vh - 107px);

    .grid-container {
      height: calc(100vh - 107px);
      max-height: calc(100vh - 107px);
    }
    .sidebar {
      height: calc(100vh - 107px);
      top: 107px;
      padding-top: 115px !important;
    }
    .container-wrap {
      padding-top: 20px;
    }
    .all-members {
      .ps {
        max-height: calc(100vh - 195px);
      }
    }
    .map-wrapper {
      height: calc(100vh - 107px) !important;
    }
  }
  .main-overlay {
    position: fixed;
    left: 359px;
    top: 73px;
    height: 100%;
    z-index: 1;
    background-color: rgba(0, 0, 0, 0.5);
    width: calc(100% - 359px);
  }
}
</style>
